<script lang="ts" setup>
// Import styles.
import 'vidstack/player/styles/base.css';
// Register elements.
import 'vidstack/player';
import 'vidstack/player/ui';
import 'vidstack/icons';


import { isHLSProvider, type MediaCanPlayEvent, type MediaProviderChangeEvent } from 'vidstack';
import type { MediaPlayerElement } from 'vidstack/elements';
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { PlayerStore } from '@/front/stores/player_store.js';
const playerStore = PlayerStore();

import VideoLayout from '@/front/components/player/components/layouts/VideoLayout.vue';

const props = defineProps({
  controls: {
    type: Boolean,
    default: true,
  },
  loop: {
    type: Boolean,
    default: false,
  },
})


const domVideo = ref<HTMLVideoElement>();
import { VideoStore } from '@/front/stores/video_store.js';
const videoStore = VideoStore();
import { UploadStore } from '@/front/stores/upload_store.js';
const uploadStore = UploadStore();

const $loopPlayer = ref<MediaPlayerElement>();
const $src = ref('');
let recenterRafId: number | null = null;

const isIOSWebView = computed(() => {
  const userAgent = navigator.userAgent || '';
  const isIOS = userAgent.includes('iPhone') || userAgent.includes('iPad') || userAgent.includes('iPod');
  return isIOS && playerStore.isWebView;
});


// Method to extract the video source from the video object
function getVideoSource(video) {
  return uploadStore.uploadFile && uploadStore.uploadSource
    ? uploadStore.uploadSource
    : video.source?.hls || video.source?.mp4 || video.source?.s3 || '';
}

const emit = defineEmits(['ended'])

onMounted(() => {
  // const { currentSrc, controlsHidden, currentTime, duration, fullscreen, playing, qualities, quality, seekableEnd, seekableStart, textTrack, volume, waiting, controlsVisible, ended, error, height, muted, paused, playbackRate, seeking, started, width } = $player.value?.state
  $src.value = getVideoSource(videoStore.video)
  log.info(3, "🌜 LoopingPlayer $src", $src.value)
  window.addEventListener('resize', recenterLoopVideo);
  window.addEventListener('orientationchange', recenterLoopVideo);
  window.visualViewport?.addEventListener('resize', recenterLoopVideo);
  window.visualViewport?.addEventListener('scroll', recenterLoopVideo);

  // $player.value?.subscribe(({ currentSrc, controlsHidden, fullscreen, playing, qualities, quality, seekableEnd, seekableStart, textTrack, volume, waiting, controlsVisible, duration, ended, error, height, muted, paused, playbackRate, seeking, started, width }) => {
  //   clipStore.setPlayerState({ currentSrc, controlsHidden, fullscreen, playing, qualities, quality, seekableEnd, seekableStart, textTrack, volume, waiting, controlsVisible, duration, ended, error, height, muted, paused, playbackRate, seeking, started, width })
  // });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', recenterLoopVideo);
  window.removeEventListener('orientationchange', recenterLoopVideo);
  window.visualViewport?.removeEventListener('resize', recenterLoopVideo);
  window.visualViewport?.removeEventListener('scroll', recenterLoopVideo);
  if (recenterRafId !== null) {
    cancelAnimationFrame(recenterRafId);
    recenterRafId = null;
  }
});

function recenterLoopVideo() {
  if (!isIOSWebView.value) {
    return;
  }
  if (recenterRafId !== null) {
    cancelAnimationFrame(recenterRafId);
  }
  recenterRafId = requestAnimationFrame(() => {
    recenterRafId = null;
    const provider = document.getElementById('loop-media-provider');
    const video = provider?.querySelector('video') as HTMLVideoElement | null;
    if (!provider || !video) {
      return;
    }

    const containerRect = provider.getBoundingClientRect();
    const videoRect = video.getBoundingClientRect();
    if (!containerRect.width || !containerRect.height || !videoRect.width || !videoRect.height) {
      return;
    }

    const centerXDelta = (containerRect.left + containerRect.width / 2) - (videoRect.left + videoRect.width / 2);
    const centerYDelta = (containerRect.top + containerRect.height / 2) - (videoRect.top + videoRect.height / 2);
    const shiftX = Math.abs(centerXDelta) < 0.5 ? 0 : Number(centerXDelta.toFixed(2));
    const shiftY = Math.abs(centerYDelta) < 0.5 ? 0 : Number(centerYDelta.toFixed(2));

    provider.style.setProperty('--loop-webview-shift-x', `${shiftX}px`);
    provider.style.setProperty('--loop-webview-shift-y', `${shiftY}px`);
  });
}

function onProviderChange(event: MediaProviderChangeEvent) {
  const provider = event.detail;
  // We can configure provider's here.
  if (isHLSProvider(provider)) {
    provider.config = {};
  }
}

// function onEnded() {
//   log.info(5,"ClipPlayer onEnded emitting ended")
//   emit('ended')
// }

  //

// We can listen for the `can-play` event to be notified when the player is ready.
function onCanPlay(event: MediaCanPlayEvent) {
  setTimeout(() => {
    domVideo.value = document.querySelector('#loop-player video')
    if (!domVideo.value) {
      return;
    }
    // log.info(5,"LoopingPlayer onCanPlay", domVideo.value)
    domVideo.value.muted = true
    domVideo.value.volume = 0
    domVideo.value.play()
    recenterLoopVideo()
    setTimeout(recenterLoopVideo, 80)
    setTimeout(recenterLoopVideo, 250)
  }, 10)
  // ...
}

// onBeforeUnmount(() => {
//   clipStore.clear()
// })

function onTimeUpdate(event: MediaTimeUpdateEvent) {
  const t = Math.floor(event.detail.currentTime)
  if (t > 5) {
    domVideo.value.currentTime = 0
  }
}

const getCrossOrigin = computed(() => {
  // log.info(5,"🌜 Player getCrossOrigin", $src.value)
  if ($src.value.includes("cloudfront")) {
    return false
  } else if ($src.value.includes("panopto")) {
    return 'anonymous'
  } else {
    return 'anonymous'
  }
});

// const onLoopClick = () => {
//   log.info(5,"LoopingPlayer onClipClick")
//   playerStore.togglePlayer()
//   return
//   // emit('click')
// }

const onError = (event) => {
  log.info(5,"🌜 LoopingPlayer onError", event);
  if (videoStore.video.source.backup_hls && $loopPlayer.value.src != videoStore.video.source.backup_hls) {
    setTimeout(() => {
      log.info(5,"🌜 LoopingPlayer onError, switching to backup HLS", videoStore.video.source.backup_hls);
      if ($loopPlayer.value) {
        $loopPlayer.value.src = videoStore.video.source.backup_hls;
      }
    }, 50)
  } else {
    playerStore.error = event.detail.message;
  }
};

const loadStrategy = computed(() => {
  if (playerStore.isMobile && !playerStore.isWebView) {
    return "idle";
  }
  return "eager";
});

</script>

<template>
  <media-player
    :load="loadStrategy"
    class="w-full h-full bg-black text-white font-sans overflow-hidden ring-media-focus data-[focus]:ring-4"
    :class="{ 'cover-mode': videoStore.video?.design?.cover_mode, 'ios-webview-center-fix': isIOSWebView }"
    :src="$src"
    :crossOrigin="getCrossOrigin"
    playsInline
    loop
    :muted="true"
    @ended="onEnded"
    @provider-change="onProviderChange"
    @time-update="onTimeUpdate"
    @error="onError"
    @can-play="onCanPlay"
    ref="$loopPlayer"
    id="loop-player"
  >
    <media-provider id="loop-media-provider">
    </media-provider>
  </media-player>
</template>

<style>
#loop-player {
  width: 100%;
  height: 100%;
}

#loop-media-provider {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

#loop-media-provider video {
  position: absolute;
  top: 50%;
  left: 50%;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  margin: 0;
  object-fit: contain;
  object-position: center center;
  transform: translate3d(-50%, -50%, 0);
  -webkit-transform: translate3d(-50%, -50%, 0);
  will-change: transform;
}

#loop-player.ios-webview-center-fix #loop-media-provider video {
  transform: translate3d(
    calc(-50% + var(--loop-webview-shift-x, 0px)),
    calc(-50% + var(--loop-webview-shift-y, 0px)),
    0
  );
  -webkit-transform: translate3d(
    calc(-50% + var(--loop-webview-shift-x, 0px)),
    calc(-50% + var(--loop-webview-shift-y, 0px)),
    0
  );
}

#loop-player.cover-mode #loop-media-provider video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  object-fit: cover;
  transform: none;
  -webkit-transform: none;
}
</style>
